import { format } from "date-fns";
import { formatPipelineHealthMoney } from "./money";
import { roundedOutlookCount } from "../target-outlook/readout";
import { creationComparisonDates, hasSupportedForecastComparison, type PipelineHealthData, type PipelineHealthReadout } from "./presentation";
import { PIPELINE_HEALTH_OVERVIEW_COPY as COPY, type PipelineHealthAnalysis } from "./copy";
import type { CreationPlanning } from "./creationPlanning";

type Observation = { title: string; detail: string; scope?: string; analysis: PipelineHealthAnalysis };
const moneyFor = (data: PipelineHealthData) => (value: number) => formatPipelineHealthMoney(value, data.model.companyCurrency);

/** Presentation of the existing ARR bridge, not a replacement Forecast target. */
export function coveragePlanReference(data: PipelineHealthData, planning?: CreationPlanning) {
  if (planning?.bridge?.state !== "ready" || !data.months?.size || !planning.plan || planning.plan.money.currency !== data.model.companyCurrency?.code) return null;
  const rows = planning.bridge.value.months.filter(row => data.months.has(row.month));
  if (!rows.length) return null;
  const period = rows.length === 1 ? format(new Date(`${rows[0].month}-01T12:00:00`), "MMM yyyy")
    : `${format(new Date(`${rows[0].month}-01T12:00:00`), "MMM")}–${format(new Date(`${rows.at(-1)!.month}-01T12:00:00`), "MMM yyyy")}`;
  const complete = rows.length === data.months.size;
  return {
    detail: `${planning.plan.confirmation === "provisional" ? "Provisional plan" : "Growth plan"}: ${moneyFor(data)(rows.reduce((sum, row) => sum + row.newSales, 0))} in new ARR for ${period}. ${complete ? "Derived new-ARR requirement for this period, from the growth plan." : "The plan does not cover the whole selected period."}`,
    limit: `${complete ? "The plan covers the selected months" : `New-ARR requirements begin ${format(new Date(`${planning.bridge.value.months[0].month}-01T12:00:00`), "MMM yyyy")}`}; the existing outlook can include other business types. No comparable target-attainment assessment is calculated.`,
  };
}

export function overviewChange(data: PipelineHealthData, readout: PipelineHealthReadout | null): Observation | null {
  if (data.position?.historical) return null;
  const money = moneyFor(data);
  const movement = data.movement;
  if (hasSupportedForecastComparison(data) && movement?.available && data.previousSnapshot.data && movement.currentForecast !== movement.snapshotForecast) return {
    title: "The selected-period forecast has changed",
    detail: `${money(movement.currentForecast)} now versus ${money(movement.snapshotForecast)} at the comparison date.`,
    scope: `${format(new Date(data.previousSnapshot.data.asOf), "d MMM yyyy")}–${format(data.asOf, "d MMM yyyy")} · reconstructed comparison`,
    analysis: "coverage",
  };
  const pipeline = readout?.decision.pipeline;
  // Consume the existing creation-pace effect and its existing materiality gate.
  if (!pipeline || !data.position || data.position.target <= 0 || pipeline.trendArrDifference === 0
    || Math.abs(pipeline.trendArrDifference) < readout!.decision.minimumImpact) return null;
  const comparison = creationComparisonDates(data.asOf);
  return {
    title: `${money(Math.abs(pipeline.trendArrDifference))} ${pipeline.trendArrDifference > 0 ? "more" : "less"} expected from future creation`,
    detail: `Compared with unchanged prior-year creation pace. ${pipeline.trendArrDifference > 0 ? "Already included in the outlook, not additional upside." : "The slower pace is already reflected in the outlook."}`,
    scope: `Creation comparison: ${comparison.current} vs ${comparison.prior}`,
    analysis: "pipeline-creation",
  };
}

export function overviewPriority(data: PipelineHealthData, readout: PipelineHealthReadout | null): Observation | null {
  if (!data.position || data.position.historical) return null;
  if (data.position.target <= 0) return { title: "Establish the revenue target", detail: "Coverage can then be judged against the business goal.", analysis: "coverage" };
  if (!data.complete) return { title: "Verify the creation-history inputs", detail: data.futureHistory?.future.reason ?? "A full outlook requires a supported future-creation estimate.", analysis: "pipeline-creation" };
  if (!readout) return null;
  const pipeline = readout.decision.pipeline;
  if (readout.decision.primaryFocus === "sustain-creation" && pipeline) return {
    title: COPY.sustainCreation,
    detail: `About ${roundedOutlookCount(pipeline.createdDeals)} new opportunities are assumed between ${format(data.asOf, "d MMM")} and ${format(data.range.end, "d MMM yyyy")}.`,
    scope: COPY.baselineVolume,
    analysis: "pipeline-creation",
  };
  if (readout.futurePrimary && readout.actionDetail) return { title: "Confirm capacity for additional creation", detail: readout.actionDetail, analysis: "pipeline-creation" };
  const action = readout.packet.rankedMaterialActions[0];
  return action ? {
    title: action.opportunities.map(opportunity => opportunity.name).join(", "),
    detail: `${action.condition}. ${action.additional} additional contribution if the condition is met.`,
    analysis: "coverage",
  } : null;
}
